import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Building2, Plus, Trash2, ChevronRight } from "lucide-react";
import MandanteLogo from "@/components/MandanteLogo";
import { useConfirm } from "@/components/ConfirmProvider";

export default function MandantesList() {
  const confirmAction = useConfirm();
  const { user } = useAuth();
  const nav = useNavigate();
  const [mandantes, setMandantes] = useState([]);
  const [workers, setWorkers] = useState([]);
  const [newMandante, setNewMandante] = useState("");
  const canEdit = ["ADMIN", "HR_MANAGER"].includes(user?.role);

  const load = () => {
    api.get("/mandantes").then((r) => setMandantes(r.data));
    api.get("/workers").then((r) => setWorkers(r.data));
  };
  useEffect(() => { load(); }, []);

  const countMandante = (name) => workers.filter((w) => (w.mandante || w.company) === name).length;
  const avgProgress = (name) => {
    const list = workers.filter((w) => (w.mandante || w.company) === name);
    if (!list.length) return 0;
    return Math.round(list.reduce((s, w) => s + (w.progress_percentage || 0), 0) / list.length);
  };

  const addMandante = async () => {
    if (!newMandante.trim()) return;
    try { await api.post("/mandantes", { name: newMandante }); setNewMandante(""); toast.success("Mandante creado"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo crear"); }
  };
  const removeMandante = async (e, id, name) => {
    e.stopPropagation();
    const ok = await confirmAction({ title: `Eliminar mandante "${name}"`,
      description: "Se eliminará el mandante y toda su configuración (contratos, cargos, documentos y timeline). Esta acción no se puede deshacer.",
      confirmText: "Eliminar mandante", variant: "danger" });
    if (!ok) return;
    try { await api.delete(`/mandantes/${id}`); toast.success("Mandante eliminado"); load(); }
    catch (err) { toast.error(err.response?.data?.detail || "No se pudo eliminar"); }
  };

  const riskColor = (p) => p >= 80 ? "bg-emerald-500" : p >= 50 ? "bg-amber-500" : "bg-red-500";

  return (
    <div className="space-y-6">
      <div>
        <h1 className="font-display text-3xl font-bold text-slate-900">Mandantes · Configuración</h1>
        <p className="text-slate-500 mt-1">Configura cada mandante: contratos, estándares de documentos y cargos. La carga de archivos se hace en <Link to="/acreditacion" className="text-indigo-600 hover:underline">Acreditación</Link>.</p>
      </div>

      {canEdit && (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-4 flex gap-2 max-w-xl">
          <input data-testid="new-mandante-input" value={newMandante} onChange={(e) => setNewMandante(e.target.value)} placeholder="Nuevo mandante"
            onKeyDown={(e) => e.key === "Enter" && addMandante()}
            className="flex-1 px-3 py-2 rounded-lg border border-slate-200 focus:border-indigo-400 outline-none text-sm" />
          <button data-testid="add-mandante-button" onClick={addMandante} className="inline-flex items-center gap-1.5 bg-indigo-600 hover:bg-indigo-700 text-white text-sm font-semibold px-4 py-2 rounded-lg">
            <Plus size={15} /> Crear mandante
          </button>
        </div>
      )}

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
        {mandantes.length === 0 ? (
          <div className="p-10 text-center">
            <Building2 size={32} className="text-slate-300 mx-auto mb-2" />
            <p className="text-slate-500 text-sm">Aún no hay mandantes.{canEdit ? " Crea el primero arriba." : ""}</p>
          </div>
        ) : (
          <table data-testid="mandantes-table" className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-100">
              <tr className="text-left text-[10px] uppercase tracking-wide text-slate-500">
                <th className="px-5 py-3">Mandante</th>
                <th className="px-5 py-3 text-right whitespace-nowrap">Contratos</th>
                <th className="px-5 py-3 text-right whitespace-nowrap">Trab.</th>
                <th className="px-5 py-3">Avance</th>
                {canEdit && <th className="px-5 py-3 text-right w-20"></th>}
                <th className="px-5 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {mandantes.map((m) => {
                const prog = avgProgress(m.name);
                return (
                  <tr key={m.id} data-testid={`mandante-row-${m.id}`} className="border-t border-slate-50 hover:bg-indigo-50/40 cursor-pointer transition-colors" onClick={() => nav(`/mandantes/${m.id}`)}>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-3">
                        <MandanteLogo mandante={m} size="sm" />
                        <span className="font-semibold text-slate-800">{m.name}</span>
                      </div>
                    </td>
                    <td className="px-5 py-3 text-right font-mono text-slate-700">{(m.contratos || []).length}</td>
                    <td className="px-5 py-3 text-right font-mono text-slate-700">{countMandante(m.name)}</td>
                    <td className="px-5 py-3">
                      <div className="flex items-center gap-2">
                        <div className="h-1.5 w-24 bg-slate-100 rounded-full overflow-hidden">
                          <div className={`h-full ${riskColor(prog)}`} style={{ width: `${prog}%` }} />
                        </div>
                        <span className="font-semibold text-blue-600 w-10 text-right">{prog}%</span>
                      </div>
                    </td>
                    {canEdit && (
                      <td className="px-5 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                        <button data-testid={`del-mandante-${m.id}`} onClick={(e) => removeMandante(e, m.id, m.name)}
                          className="text-slate-400 hover:text-red-600" title="Eliminar"><Trash2 size={15} /></button>
                      </td>
                    )}
                    <td className="px-5 py-3 text-right"><ChevronRight size={16} className="text-slate-300" /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}
